/**
 * @documental
 * @export Badge
 * @import
 * import { Badge } from "capitalrx-components-2.0/lib/general";
 */
import { FC, ReactNode } from "react";

import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import classNames from "classnames";

import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Button, ButtonClickEventHandler } from "../Button";
import { Tag, TagProps } from "../Tag";

import tokenStyles from "./styles.module.css";

/**
 * The `Badge` component properties.
 */
export interface BadgeProps extends TagProps {
  /**
   * Called when the close button is clicked.
   */
  readonly onClick?: ButtonClickEventHandler;

  /**
   * Indicates whether the badge is disabled.
   */
  readonly disabled?: boolean;

  /**
   * The badge tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * The close button tooltip.
   */
  readonly buttonTooltip?: ReactNode;
}

const useResolvedBadgeStyles = createComponentStyleHook<BadgeProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "badge",
});

/**
 * A badge.
 */
export const Badge: FC<BadgeProps> = (props) => {
  const { children, tooltip, buttonTooltip, onClick, disabled = false, type = "info", inverted = false } = props;
  const { mode, isLegacy, styles, StyleModeBoundary } = useResolvedBadgeStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [classNames(styles.Badge, "badge")],
    testIdPrefix: "badge",
  });

  const { className: _internalClassName, style: _internalStyle, ...tagBaseProps } = getBasePropsFromHtmlProps(baseHtmlProps);

  return (
    <StyleModeBoundary mode={mode}>
      <Tag
        {...props}
        {...tagBaseProps}
        {...(isLegacy ? { className: props.className, style: props.style } : {})}
        tooltip={tooltip}
        aria-live={disabled ? undefined : "polite"}
        aria-disabled={disabled}
      >
        <Shelf
          className={classNames(
            {
              [styles.Shelf]: !disabled,
            },
            type,
            { inverted },
          )}
          spacing="mini"
        >
          <span className={classNames(styles.Content, "content")}>{children}</span>
          {!disabled && (
            // TODO: add key handlers to buttons
            <Button
              className={classNames(styles.CloseButton, type, { inverted })}
              appearance="text"
              icon="x"
              size="small"
              tooltip={buttonTooltip}
              onClick={onClick}
              aria-label="Close Badge"
            />
          )}
        </Shelf>
      </Tag>
    </StyleModeBoundary>
  );
};
